import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import html2pdf from 'html2pdf.js';
import { Crown, TrendingDown, MessageSquareOff } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const paymentMethods = [
  {
    id: 'instant-eft',
    name: 'INSTANT EFT',
    subtitle: 'Preferred Method',
    color: 'from-blue-500 to-blue-700',
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
    badge: 'INSTANT & COST-EFFECTIVE',
    badgeColor: 'bg-blue-600',
    description: 'Front of the Queue: Payment clears instantly, securing your spot in our workflow immediately.',
    benefits: [
      'Total Security: Encrypted, bank-level security, no card details required.',
      'No Extra Costs: No bank fees, no admin fees.',
      'Priority Service: No 48-hour waiting period for funds to clear—we start working <b><u>today!</u></b>',
      'No Paperwork: Proof of payment is automatic—no need to email us.'
    ],
    pricing: {
      label: 'Total Investment',
      amount: 'R750', // This will be dynamically updated
      note: '' // Removed the note
    },
    paymentLink: 'https://paystack.com/buy/personal-tax-services-bronze-all-in-discount',
    isDebitOrder: false,
    isDynamic: true // Flag to indicate this card needs dynamic pricing
  },
  {
    id: 'card-payment',
    name: 'CARD PAYMENT',
    subtitle: 'Buy Now, Pay Later',
    color: 'from-purple-500 to-purple-700',
    borderColor: 'border-purple-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-800',
    badge: 'CONVENIENT',
    badgeColor: 'bg-purple-600',
    description: 'Convenient & Cashflow-friendly: Activate your tax file using your credit or debit card for convenience and budget management. Pay on credit terms!',
    benefits: [
      'Buy Now, Pay Later: Use your credit facility to get your taxes done today and pay the bank later.',
      'Earn Rewards: Maximize your return on investment by earning bank loyalty points/eBucks on this swipe.',
      'Total Control: You initiate the transaction—with no recurring debit orders or surprises.',
      'Priority Service: No 48-hour waiting period for funds to clear—we start working <b><u>today!</u></b>'
    ],
    pricing: {
      label: 'Total Investment',
      amount: 'R765', // This will be dynamically updated
      note: 'Includes a R15 card processing fee due to high merchant banking charges.'
    },
    paymentLink: 'https://paystack.com/buy/personal-tax-services-initiation-fee-card',
    isDebitOrder: false,
    isDynamic: true, // Flag to indicate this card needs dynamic pricing
    cardFee: 15 // Additional card processing fee
  },
  {
    id: 'debit-order',
    name: 'DEBIT ORDER',
    subtitle: 'Deferred Payment',
    color: 'from-teal-500 to-teal-700',
    borderColor: 'border-teal-500',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-800',
    badge: 'SET & FORGET',
    badgeColor: 'bg-teal-600',
    description: 'Pay on Salary Day: Activate your tax file and reserve your spot without touching your bank balance today—payment runs on your future chosen date.',
    benefits: [
      'Book Now, Pay Later: Keep your cash today—payment is only deducted on your chosen date.',
      'Set & Forget: Zero admin required—simply sign the mandate once and we handle the rest automatically.',
      'Stress-Free: Eliminates the hassle of logging into banking apps or remembering to make payment.',
      'Automated Convenience: The nominal R75 service fee ensures a fully automated, hands-off experience.'
    ],
    pricing: {
      label: 'Total Investment',
      amount: 'R825', // This will be dynamically updated
      note: 'Want to save R50? We reduce the service charge to just R25 if you elect to debit the full amount (Initiation Fee + Submission Fee + Express Fee <small><u>(if applicable)</u></small>) in a single debit run.<br><br>Note: Tax services commence immediately once the debit order run was successful.'
    },
    paymentLink: null,
    isDebitOrder: true,
    isDynamic: true, // Flag to indicate this card needs dynamic pricing
    debitFee: 75 // Additional debit order service fee
  },
  {
    id: 'layby-combo',
    name: 'LAY-BY',
    subtitle: 'Flexible Installment Plan',
    color: 'from-orange-500 to-orange-700',
    borderColor: 'border-orange-500',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-800',
    badge: 'SPLIT PAYMENT',
    badgeColor: 'bg-orange-600',
    description: 'Secure Now, Pay Gradually: Lock-in your tax services today without the pressure of a full upfront payment, and spread the cost over a timeline that works for you.',
    benefits: [
      'Pocket-Friendly: Split the total cost into 2, 3 or 4 smaller payments (weekly/monthly) that don\'t break the bank.',
      'One Fixed Fee: A single, once-off R150 service fee covers your <em><u>entire</u></em> cart (Initiation, Express & Submission Fees)—no extra admin costs later.',
      '0% Interest: Unlike credit card payments, this plan is interest-free. You only pay the fixed service fee, regardless of how long you take to pay.',
      'Guaranteed Activation: While work commences only after the final payment, your spot and price are secured the moment you start your plan.'
    ],
    pricing: {
      label: 'Total Investment',
      amount: 'R550/month', // This will be dynamically updated
      note: 'Note: This is a pre-payment plan. Tax services commence immediately once the final installment is received.<br><br><b>Important:</b> Please commit to the schedule; cancelling this plan forfeits all installments paid to date. No refunds.'
    },
    paymentLink: 'https://paystack.shop/pay/personal-tax-services-laybye-deal',
    isDebitOrder: false,
    isDynamic: true, // Flag to indicate this card needs dynamic pricing
    splitFee: 150 // Additional split payment fee
  },
  {
    id: 'manual-eft',
    name: 'MANUAL EFT',
    subtitle: 'Client-Initiated Bank Transfer',
    color: 'from-gray-400 to-gray-600',
    borderColor: 'border-gray-400',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    badge: 'SELF-MANAGED',
    badgeColor: 'bg-gray-500',
    description: 'Self-Service Payment: Log in to your banking app to pay. Proof of payment required within 8 hours.',
    benefits: [
      'Familiar Interface: Perform the bank transfer comfortably within your own banking app environment.',
      'Manual Control: You manually input the beneficiary details and payment amount yourself.',
      'No Integrations: A traditional transfer that requires no 3rd-party payment gateway authorization.'
    ],
    warnings: [
      'Strict Time Limit: Service agreement and quote expires in 8 hours.',
      'Manual Proof: You must email the PDF Proof of Payment to us manually.',
      'Wait Period: Work only commences once funds reflect in our account (approx. 48 hours).'
    ],
    pricing: {
      label: 'Total Investment',
      amount: 'R750 or R1,425', // This will be dynamically updated
      note: ''
    },
    bankDetails: {
      bank: 'First National Bank',
      accountName: 'TC Auditors (Pty) Ltd',
      accountNumber: '62560222871',
      branchCode: '250655',
      reference: 'Your ID Number'
    },
    paymentLink: null,
    isManualEFT: true,
    isDebitOrder: false,
    isDynamic: true // Flag to indicate this card needs dynamic pricing
  }
];

const PageSix = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};
  
  // Get formId from previous page
  const formId = clientData.formId;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const [selectedMethod, setSelectedMethod] = useState(clientData.selectedPaymentMethod || null);
  const [selectedFeeOption, setSelectedFeeOption] = useState(clientData.selectedFeeOption || null); // Track what fees client wants to pay
  const [selectedInstallment, setSelectedInstallment] = useState(null); // Track installment option for LAY-BY
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [showPaymentCompleteDialog, setShowPaymentCompleteDialog] = useState(false);
  const [showEftWarningModal, setShowEftWarningModal] = useState(false);
  const [eftWarningAcknowledged, setEftWarningAcknowledged] = useState(false);
  const [showManualEFTDialog, setShowManualEFTDialog] = useState(false);
  const [showUploadSuccessDialog, setShowUploadSuccessDialog] = useState(false);
  const [showExpressUpgradeDialog, setShowExpressUpgradeDialog] = useState(false); // Track express upgrade modal
  const [isUpgradingToExpress, setIsUpgradingToExpress] = useState(false); // Track upgrade loading state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes in seconds
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Dynamic pricing state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [isPricingLoaded, setIsPricingLoaded] = useState(false);
  
  // Get backend URL from environment
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pricing`);
        const data = await response.json();
        setPricingConfig(data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Use default pricing on error
        setPricingConfig({
          packages: {
            bronze: { initiationFee: 750, submissionFee: 750, expressFee: 250, refundFee: 0 },
            silver: { initiationFee: 750, submissionFee: 750, expressFee: 500, refundFee: 10 },
            gold: { initiationFee: 750, submissionFee: 1500, expressFee: 750, refundFee: 10 },
            platinum: { initiationFee: 750, submissionFee: 2500, expressFee: 1500, refundFee: 10 }
          },
          paymentSurcharges: { cardFee: 15, debitOrderFee: 75, laybyFee: 150 }
        });
      } finally {
        setIsPricingLoaded(true);
      }
    };
    
    fetchPricing();
  }, [BACKEND_URL]);
  
  // Check if express was selected on Page 5
  const expressSelected = clientData.selectedUrgency === 'express';
  
  // Calculate pricing based on Page 5 selections and fetched pricing config
  const calculatePricing = () => {
    if (!pricingConfig) return { initiationFee: 0, submissionFee: 0, expressFee: 0, expressFeeAmount: 0, subtotal: 0, discount: 0, total: 0 };
    
    const selectedPackage = clientData.selectedPackage;
    const pkgPricing = pricingConfig.packages?.[selectedPackage] || pricingConfig.packages?.bronze || {
      initiationFee: 750, submissionFee: 750, expressFee: 250
    };
    
    const initiationFee = pkgPricing.initiationFee;
    const submissionFee = pkgPricing.submissionFee;
    const expressFee = expressSelected ? pkgPricing.expressFee : 0;
    const expressFeeAmount = pkgPricing.expressFee; // Always available for upgrade display
    
    const subtotal = initiationFee + submissionFee + expressFee;
    const discount = Math.round(subtotal * 0.05); // 5% discount
    const total = subtotal - discount;
    
    return {
      initiationFee,
      submissionFee,
      expressFee,
      expressFeeAmount, // Always shows the express fee amount regardless of selection
      subtotal,
      discount,
      total
    };
  };
  
  const pricing = calculatePricing();
  
  // Get dynamic surcharge fees from pricing config
  const getSurcharges = () => {
    if (!pricingConfig?.paymentSurcharges) {
      return { cardFee: 15, debitOrderFee: 75, debitOrderDiscount: 50, laybyFee: 150 };
    }
    return pricingConfig.paymentSurcharges;
  };
  
  const surcharges = getSurcharges();
  
  // Helper function to format currency with commas
  const formatCurrency = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to format currency with 2 decimal places
  const formatCurrencyFull = (amount) => {
    if (amount === undefined || amount === null) return 'R0.00';
    return `R${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate discounted debit order fee
  const discountedDebitFee = surcharges.debitOrderFee - (surcharges.debitOrderDiscount || 0);

  // Generate dynamic benefits text for Debit Order card
  const getDebitOrderBenefits = () => [
    'Book Now, Pay Later: Keep your cash today—payment is only deducted on your chosen date.',
    'Set & Forget: Zero admin required—simply sign the mandate once and we handle the rest automatically.',
    'Stress-Free: Eliminates the hassle of logging into banking apps or remembering to make payment.',
    `Automated Convenience: The nominal ${formatCurrencyFull(surcharges.debitOrderFee)} service fee ensures a fully automated, hands-off experience.`
  ];

  // Generate dynamic note for Debit Order card
  const getDebitOrderNote = () => {
    const saving = surcharges.debitOrderDiscount || 50;
    const discountedFee = surcharges.debitOrderFee - saving;
    return `Want to save ${formatCurrencyFull(saving)}? We reduce the service charge to just ${formatCurrencyFull(discountedFee)} if you elect to debit the full amount (Initiation Fee + Submission Fee + Express Fee <small><u>(if applicable)</u></small>) in a single debit run.<br><br>Note: Tax services commence immediately once the debit order run was successful.`;
  };

  // Generate dynamic benefits text for Lay-by card
  const getLaybyBenefits = () => [
    'Pocket-Friendly: Split the total cost into 2, 3 or 4 smaller payments (weekly/monthly) that don\'t break the bank.',
    `One Fixed Fee: A single, once-off ${formatCurrencyFull(surcharges.laybyFee)} service fee covers your <em><u>entire</u></em> cart (Initiation, Express & Submission Fees)—no extra admin costs later.`,
    '0% Interest: Unlike credit card payments, this plan is interest-free. You only pay the fixed service fee, regardless of how long you take to pay.',
    'Guaranteed Activation: While work commences only after the final payment, your spot and price are secured the moment you start your plan.'
  ];

  // Generate dynamic note for Card payment
  const getCardPaymentNote = () => {
    return `Includes a ${formatCurrencyFull(surcharges.cardFee)} card processing fee due to high merchant banking charges.`;
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update state when returning from Page 7 with preserved data
  useEffect(() => {
    if (clientData.selectedPaymentMethod !== undefined) {
      setSelectedMethod(clientData.selectedPaymentMethod);
    }
    if (clientData.selectedFeeOption !== undefined) {
      setSelectedFeeOption(clientData.selectedFeeOption);
    }
  }, [location.key]);

  // Timer countdown effect
  useEffect(() => {
    if (showTimerDialog && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setShowTimerDialog(false);
            setShowPaymentCompleteDialog(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [showTimerDialog, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSelectedMethodDetails = () => {
    return paymentMethods.find(m => m.id === selectedMethod);
  };
  
  // Helper function to get the surcharge fee for a payment method
  const getSurchargeForMethod = (methodId) => {
    if (!surcharges) return 0;
    switch (methodId) {
      case 'card-payment':
        return surcharges.cardFee || 0;
      case 'debit-order':
        return surcharges.debitOrderFee || 0;
      case 'layby-combo':
        return surcharges.laybyFee || 0;
      default:
        return 0;
    }
  };

  // Helper function to get the actual displayed amount for the selected method
  const getDisplayedAmount = () => {
    const method = getSelectedMethodDetails();
    if (!method) return '';
    
    // For dynamic cards, calculate the amount based on selectedFeeOption
    if (method.isDynamic && selectedFeeOption) {
      let baseAmount = 0;
      
      if (selectedFeeOption === 'initiation-only') {
        baseAmount = pricing.initiationFee;
      } else if (selectedFeeOption === 'initiation-express') {
        baseAmount = pricing.initiationFee + pricing.expressFee;
      } else if (selectedFeeOption === 'all-fees') {
        baseAmount = pricing.total;
      }
      
      // Get surcharge for this payment method
      const methodSurcharge = getSurchargeForMethod(method.id);
      
      // Add surcharge if applicable
      if (methodSurcharge > 0) {
        const total = baseAmount + methodSurcharge;
        return `R${formatCurrency(baseAmount)} + R${methodSurcharge} = R${formatCurrency(total)}`;
      } else {
        return `R${formatCurrency(baseAmount)}`;
      }
    }
    
    // For static cards, return the pricing.amount as is
    return method.pricing.amount;
  };

  // Helper function to get dynamic fee coverage text for LAY-BY based on upfront selection
  const getLayByFeeText = () => {
    if (!selectedFeeOption) return 'entire cart (Initiation Fee + Express Fee + Submission Fee)';
    
    if (selectedFeeOption === 'initiation-only') {
      return 'entire cart (Initiation Fee)';
    } else if (selectedFeeOption === 'initiation-express') {
      return 'entire cart (Initiation Fee + Express Fee)';
    } else if (selectedFeeOption === 'all-fees') {
      // Check if express is selected to determine if we include it
      if (expressSelected) {
        return 'entire cart (Initiation Fee + Express Fee + Submission Fee)';
      } else {
        return 'entire cart (Initiation Fee + Submission Fee)';
      }
    }
    
    return 'entire cart (Initiation Fee + Express Fee + Submission Fee)';
  };

  // Helper function to get the correct payment link dynamically
  const getDynamicPaymentLink = () => {
    const method = getSelectedMethodDetails();
    if (!method) return null;
    
    let paymentLink = method.paymentLink;
    
    // Get the selected package name (bronze, silver, gold, platinum)
    const packageName = (clientData.selectedPackage || 'bronze').toLowerCase();
    
    // Dynamic links for INSTANT EFT
    if (method.id === 'instant-eft' && selectedFeeOption) {
      if (selectedFeeOption === 'initiation-only') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-instanteft-bronze-minimum';
      } else if (selectedFeeOption === 'initiation-express') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-instanteft-bronze-expedited';
      } else if (selectedFeeOption === 'all-fees') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-instanteft-bronze-all-in-discount';
      }
    }
    
    // Dynamic links for CARD PAYMENT
    if (method.id === 'card-payment' && selectedFeeOption) {
      if (selectedFeeOption === 'initiation-only') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-card-bronze-minimum';
      } else if (selectedFeeOption === 'initiation-express') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-card-bronze-expedited';
      } else if (selectedFeeOption === 'all-fees') {
        paymentLink = 'https://paystack.com/buy/personal-tax-services-card-bronze-all-in-discount';
      }
    }
    
    // Dynamic links for LAY-BY - includes package name
    if (method.id === 'layby-combo' && selectedFeeOption && selectedInstallment) {
      let feeType;
      
      if (selectedFeeOption === 'initiation-only') {
        feeType = 'minimum';
      } else if (selectedFeeOption === 'initiation-express') {
        feeType = 'expedited';
      } else if (selectedFeeOption === 'all-fees') {
        // Check if express is selected for All-In Discount
        if (expressSelected) {
          feeType = 'all-in-discount';
        } else {
          feeType = 'all-in-discount-no-express';
        }
      }
      
      // Map installment option to URL segment
      const installmentType = selectedInstallment; // e.g., 'weekly-3', 'monthly-4'
      
      // Build LAY-BY link with package, upfront type, and installment type
      // Format: personal-tax-services-layby-{package}-{upfront}-{installment}
      // Example: personal-tax-services-layby-bronze-minimum-weekly-3
      // Example: personal-tax-services-layby-bronze-all-in-discount-weekly-3
      // Example: personal-tax-services-layby-bronze-all-in-discount-no-express-weekly-3
      paymentLink = `https://paystack.shop/pay/personal-tax-services-layby-${packageName}-${feeType}-${installmentType}`;
    }
    
    return paymentLink;
  };


  // Helper function to generate PDF and navigate to Page 7
  const generatePDFAndNavigate = async (additionalState = {}) => {
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    const element = document.querySelector('#page-pdf-content');
    if (!element) {
      overlay.remove();
      setIsGeneratingPDF(false);
      alert('Error: Could not find page content.');
      return;
    }
    
    // Store original values for restoration
    const originalStyles = new Map();
    
    const hideElement = (el) => {
      if (el) {
        originalStyles.set(el, el.style.cssText);
        el.style.display = 'none';
      }
    };
    
    const modifyStyle = (el, newStyle) => {
      if (el) {
        if (!originalStyles.has(el)) {
          originalStyles.set(el, el.style.cssText);
        }
        Object.assign(el.style, newStyle);
      }
    };
    
    try {
      // === PDF MODIFICATIONS FOR PAGE 6 ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-6 sm:py-8)
      modifyStyle(element, { paddingTop: window.matchMedia('(min-width: 640px)').matches ? '2rem' : '1.5rem' });
      
      // 0. HIDE NON-SELECTED UPFRONT PAYMENT CARDS - Only show the selected upfront payment card
      const selectedFeeOptionId = selectedFeeOption;
      if (selectedFeeOptionId) {
        element.querySelectorAll('[data-upfront-fee-id]').forEach(card => {
          if (card.getAttribute('data-upfront-fee-id') !== selectedFeeOptionId) {
            hideElement(card);
          } else {
            // Center the selected upfront card
            modifyStyle(card, {
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              transform: 'none'
            });
          }
        });
        
        // Center the upfront payment grid container
        const upfrontGrid = element.querySelector('.grid.grid-cols-1.md\\:grid-cols-3.gap-4.mb-8');
        if (upfrontGrid) {
          modifyStyle(upfrontGrid, {
            display: 'flex',
            justifyContent: 'center',
            gridTemplateColumns: '1fr'
          });
        }
      }
      
      // 1. HIDE NON-SELECTED PAYMENT METHODS - Only show the selected method card
      const selectedMethodId = selectedMethod;
      const methodGrid = element.querySelector('.grid.grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-3.xl\\:grid-cols-4');
      if (methodGrid && selectedMethodId) {
        element.querySelectorAll('[data-payment-id]').forEach(card => {
          if (card.getAttribute('data-payment-id') !== selectedMethodId) {
            hideElement(card);
          } else {
            modifyStyle(card, { 
              width: '100%', 
              maxWidth: '350px', 
              margin: '0 auto',
              transform: 'none',
              zIndex: 'auto'
            });
          }
        });
        modifyStyle(methodGrid, { 
          display: 'flex', 
          justifyContent: 'center',
          gridTemplateColumns: '1fr'
        });
      }
      
      // 2. Reduce gap between TAX SERVICES AGREEMENT and CONVENIENT PAYMENT METHODS
      element.querySelectorAll('h2.text-xl.font-bold.text-center').forEach(el => {
        modifyStyle(el, { marginBottom: '2px', paddingBottom: '0px', marginTop: '0px' });
      });
      element.querySelectorAll('p.text-center.font-semibold, h3.text-center').forEach(el => {
        modifyStyle(el, { marginTop: '2px', paddingTop: '0px', marginBottom: '4px' });
      });
      
      // Reduce spacing on the main heading section (TAX SERVICES AGREEMENT)
      element.querySelectorAll('.mb-6.pb-2').forEach(el => {
        modifyStyle(el, { marginBottom: '0px', paddingBottom: '0px' });
      });
      
      // Reduce spacing on the document header section (CONVENIENT PAYMENT METHODS)
      element.querySelectorAll('.text-center.mb-6').forEach(el => {
        modifyStyle(el, { marginBottom: '8px', marginTop: '0px', paddingTop: '0px' });
      });
      
      // Reduce card content top padding
      element.querySelectorAll('.p-4.sm\\:p-8').forEach(el => {
        modifyStyle(el, { paddingTop: '8px' });
      });
      
      // 3. Comfortable paragraph spacing
      element.querySelectorAll('p').forEach(el => {
        modifyStyle(el, { marginBottom: '10px', lineHeight: '1.5' });
      });
      
      // 4. Comfortable spacing between headings and content
      element.querySelectorAll('h1, h2, h3, h4').forEach(el => {
        modifyStyle(el, { marginTop: '8px', marginBottom: '8px' });
      });
      
      // 5. HIDE the instruction text AND the introduction paragraph
      element.querySelectorAll('p').forEach(p => {
        if (p.textContent.includes('Click on a payment method to select it')) {
          hideElement(p);
        }
        // Hide the "We offer multiple payment options..." paragraph
        if (p.textContent.includes('We offer multiple payment options to suit your needs')) {
          hideElement(p);
        }
      });
      
      // Also hide the parent div of the introduction if it becomes empty
      element.querySelectorAll('.mb-8.text-sm').forEach(div => {
        if (div.textContent.includes('We offer multiple payment options')) {
          hideElement(div);
        }
      });
      
      // 4a. HIDE "SELECT YOUR PAYMENT METHOD" heading from PDF
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('SELECT YOUR PAYMENT METHOD')) {
          hideElement(h3);
        }
      });
      
      // 5. Reduce top margin/padding but keep it comfortable
      modifyStyle(element, { paddingTop: '8px' });
      element.querySelectorAll('.py-6, .sm\\:py-8').forEach(el => {
        modifyStyle(el, { paddingTop: '8px', paddingBottom: '8px' });
      });
      
      // 5b. Fix badge visibility - move badge inside card boundary for PDF with proper spacing
      element.querySelectorAll('.absolute.-top-2').forEach(badge => {
        modifyStyle(badge, { 
          position: 'relative', 
          top: '0', 
          left: '0',
          transform: 'none',
          display: 'block',
          textAlign: 'center',
          marginBottom: '12px',
          marginTop: '4px'
        });
      });
      
      // 5c. Add spacing to the card header section (DEBIT ORDER box) so badge doesn't overlap
      element.querySelectorAll('.text-center.mb-3.pb-3').forEach(header => {
        modifyStyle(header, { 
          marginTop: '8px',
          paddingTop: '4px'
        });
      });
      
      // 6. Card padding - comfortable
      element.querySelectorAll('.p-4.sm\\:p-8').forEach(el => {
        modifyStyle(el, { padding: '16px 20px' });
      });
      
      // 7. Reduce list item spacing but keep it readable
      element.querySelectorAll('ul').forEach(ul => {
        modifyStyle(ul, { marginTop: '8px', marginBottom: '8px' });
      });
      element.querySelectorAll('li').forEach(li => {
        modifyStyle(li, { marginBottom: '6px', lineHeight: '1.5' });
      });
      
      // Hide elements marked for PDF exclusion
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      
      // Show elements marked for PDF only (like page breaks)
      element.querySelectorAll('[data-pdf-only="true"]').forEach(el => {
        if (!originalStyles.has(el)) {
          originalStyles.set(el, el.style.cssText);
        }
        modifyStyle(el, { 
          display: 'block',
          pageBreakAfter: 'always'
        });
      });
      
      // Add PDF footer
      const footer = document.createElement('div');
      footer.id = 'temp-pdf-footer';
      footer.style.cssText = `
        width: 100%;
        padding: 16px 0 8px 0;
        margin-top: 24px;
        border-top: 1px solid #e5e7eb;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      `;
      footer.innerHTML = `
        <div style="font-size: 14px; color: #374151; font-weight: 500; margin-bottom: 8px;">
          Convenient Payment Methods
        </div>
        <div style="font-size: 13px; color: #6b7280;">
          Document <strong style="color: #111827;">06</strong> of <strong style="color: #111827;">07</strong>
        </div>
        <img src="/images/TCA_Short_Logo.png" alt="TCA Logo" style="position: absolute; right: -6px; top: 50%; transform: translateY(-50%); height: 48px; width: auto; opacity: 0.8;" />
      `;
      element.appendChild(footer);
      
      // Generate PDF
      const pdfOptions = {
        margin: [6, 8, 6, 8],
        filename: `Page06_${formId || 'form'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          logging: false, 
          letterRendering: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      // Generate PDF as blob
      const pdfBlob = await html2pdf().set(pdfOptions).from(element).outputPdf('blob');
      
      // Restore all original styles
      originalStyles.forEach((originalStyle, el) => {
        el.style.cssText = originalStyle;
      });
      
      // Remove footer
      const footerEl = document.getElementById('temp-pdf-footer');
      if (footerEl) footerEl.remove();
      
      // Save PDF to backend with retry logic and verification
      const base64data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(pdfBlob);
      });
      
      const MAX_RETRIES = 3;
      let saveSuccessful = false;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(`[Page6] Save attempt ${attempt}/${MAX_RETRIES}...`);
          
          const response = await fetch(`${BACKEND_URL}/api/pdf/save-page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: formId,
              page_number: 6,
              pdf_data: base64data,
              page_title: 'Convenient Payment Methods',
              client_data: clientData
            })
          });
          
          if (!response.ok) {
            throw new Error(`Server returned ${response.status}`);
          }
          
          // Verify the PDF was saved
          const verifyResponse = await fetch(`${BACKEND_URL}/api/pdf/debug/${formId}`);
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            const page6Saved = verifyData.pages?.some(p => p.page_number === 6);
            if (page6Saved) {
              console.log('[Page6] PDF saved and verified successfully');
              saveSuccessful = true;
              break;
            } else {
              throw new Error('PDF not found after save');
            }
          }
        } catch (err) {
          console.error(`[Page6] Save attempt ${attempt} failed:`, err.message);
          if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 2000));
          }
        }
      }
      
      if (!saveSuccessful) {
        overlay.remove();
        setIsGeneratingPDF(false);
        alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
        return;
      }
      
      // Remove overlay
      overlay.remove();
      setIsGeneratingPDF(false);
      
      // Navigate to Page 7
      navigate('/page-7', { 
        state: { 
          ...clientData,
          selectedPaymentMethod: selectedMethod,
          selectedFeeOption: selectedFeeOption,
          ...additionalState
        } 
      });
      
    } catch (error) {
      console.error('[Page6] PDF generation error:', error);
      // Restore styles on error
      originalStyles.forEach((originalStyle, el) => {
        el.style.cssText = originalStyle;
      });
      const footerEl = document.getElementById('temp-pdf-footer');
      if (footerEl) footerEl.remove();
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      alert('An error occurred while generating your PDF. Please try again.');
    }
  };

  const handleMethodClick = (methodId) => {
    if (methodId === 'manual-eft' && !eftWarningAcknowledged) {
      setShowEftWarningModal(true);
      return;
    }
    setSelectedMethod(methodId);
  };

  const handleAcknowledgeEftRisk = () => {
    setEftWarningAcknowledged(true);
    setShowEftWarningModal(false);
    setSelectedMethod('manual-eft');
  };

  const handleChooseAnotherPaymentMethod = () => {
    setShowEftWarningModal(false);
  };


  const handleSubmit = async () => {
    if (!selectedMethod) return;
    
    // If payment is already completed (coming back from Page 7), navigate directly to Page 7
    if (clientData.paymentCompleted) {
      await generatePDFAndNavigate();
      return;
    }
    
    const method = getSelectedMethodDetails();
    
    if (method.isDebitOrder) {
      // Navigate directly to Page 7 for debit order
      await generatePDFAndNavigate({ paymentCompleted: true });
    } else if (method.isManualEFT) {
      // Show manual EFT details dialog
      setShowManualEFTDialog(true);
    } else {
      // Show confirmation dialog before opening payment link
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmPayment = () => {
    const method = getSelectedMethodDetails();
    setShowConfirmDialog(false);
    
    // Get dynamic payment link
    const paymentLink = getDynamicPaymentLink();
    
    // Open payment link in new window
    if (paymentLink) {
      window.open(paymentLink, '_blank');
    }
    
    // Start timer dialog
    setTimeRemaining(120);
    setShowTimerDialog(true);
  };

  const handleExtendTimer = () => {
    setShowPaymentCompleteDialog(false);
    setTimeRemaining(120);
    setShowTimerDialog(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentCompleteDialog(false);
    await generatePDFAndNavigate({ paymentCompleted: true });
  };

  const handleManualEFTConfirm = async () => {
    setShowManualEFTDialog(false);
    await generatePDFAndNavigate({ paymentCompleted: true });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please select a PDF file. Only PDF documents are accepted.');
      event.target.value = '';
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_name', clientData.firstName || '');
      formData.append('surname', clientData.surname || '');

      const response = await fetch(`${BACKEND_URL}/api/upload-pop`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUploadedFilename(result.filename);
        setShowManualEFTDialog(false);
        setShowUploadSuccessDialog(true);
      } else {
        alert(result.detail || 'Failed to upload file. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Please check your connection and try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleUploadSuccessConfirm = async () => {
    setShowUploadSuccessDialog(false);
    await generatePDFAndNavigate({ 
      paymentCompleted: true, 
      popUploaded: true, 
      popFilename: uploadedFilename 
    });
  };

  const handleBack = () => {
    navigate('/page-5', { 
      state: {
        ...clientData,
        selectedPaymentMethod: selectedMethod
      }
    });
  };

  // Show loading state while fetching pricing
  if (!isPricingLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-tca-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Payment Methods Document */}
        <Card className="w-full max-w-6xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* Document Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 underline">CONVENIENT PAYMENT METHODS</h2>
              <p className="text-gray-600 mt-2">Select your preferred payment method to activate your tax services</p>
            </div>

            {/* Fee Selection Section - NEW */}
            <div className="mb-8">
              <h3 className="font-bold text-lg underline mb-4">CHOOSE YOUR UPFRONT PAYMENT</h3>
              <div className="mb-6 text-sm sm:text-base text-gray-700 leading-relaxed">
                <p>
                  Take control of your finances by deciding what you&apos;d like to pay today. You can choose to pay just the minimum required to get started, or settle multiple fees upfront to streamline your experience and reduce future invoices.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Option 1: Initiation Fee Only */}
                <div
                  data-upfront-fee-id="initiation-only"
                  onClick={() => setSelectedFeeOption('initiation-only')}
                  className={`
                    relative cursor-pointer rounded-xl p-3 border-2 transition-all duration-300 transform overflow-visible
                    hover:scale-105 hover:shadow-lg
                    ${selectedFeeOption === 'initiation-only' ? 'border-blue-500 bg-blue-50 scale-105 shadow-lg' : 'border-gray-300 bg-white'}
                  `}
                >
                  {/* Animated Border for Selected Option */}
                  {selectedFeeOption === 'initiation-only' && (
                    <div className="absolute inset-[-3px] rounded-xl border-blue-500 border-4 animate-border-pulse"></div>
                  )}
                  
                  {selectedFeeOption === 'initiation-only' && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                        <span className="text-3xl font-bold">✓</span>
                      </div>
                    </div>
                  )}
                  <div className="relative text-center mb-2">
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-500 text-white font-bold text-xs mb-2">
                      MINIMUM
                    </span>
                    <h4 className="font-bold text-base text-gray-900">Initiation Fee Only</h4>
                  </div>
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Pay just the <span className="font-bold underline">R750</span> initiation fee to get started and secure a spot in the queue—Become compliant and know your refund amount within the next 30 days!
                  </p>
                  
                  {/* Due Later Section */}
                  <div className="mt-3 pt-3 border-t border-blue-300">
                    <p className="text-xs text-center text-gray-600 font-semibold mb-1">Due Later:</p>
                    <div className="text-xs text-center text-gray-600 space-y-0.5">
                      <p>Submission Fee: <span className="font-semibold">R{formatCurrency(pricing.submissionFee)}</span> ✅</p>
                      {clientData.selectedPackage !== 'bronze' && (
                        <p>Refund Fee: <span className="font-semibold">10% of refund</span> ✅</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Option 2: Initiation + Express Fee */}
                <div
                  data-upfront-fee-id="initiation-express"
                  onClick={() => {
                    if (expressSelected) {
                      setSelectedFeeOption('initiation-express');
                    } else {
                      // Show upgrade modal when express not selected
                      setShowExpressUpgradeDialog(true);
                    }
                  }}
                  className={`
                    relative rounded-xl p-3 border-2 transition-all duration-300 overflow-visible cursor-pointer transform hover:scale-105 hover:shadow-lg
                    ${!expressSelected ? '' : ''}
                    ${selectedFeeOption === 'initiation-express' && expressSelected ? 'border-emerald-500 bg-emerald-50 scale-105 shadow-lg' : expressSelected ? 'border-gray-300 bg-white' : ''}
                  `}
                  style={!expressSelected ? {
                    borderColor: '#D4AF37',
                    backgroundColor: '#FFF9E6'
                  } : {}}
                >
                  {/* Animated Border - Metallic Gold when not selected, Emerald when selected */}
                  {!expressSelected && (
                    <div className="absolute inset-[-3px] rounded-xl border-4 animate-border-pulse" style={{ borderColor: '#D4AF37' }}></div>
                  )}
                  {selectedFeeOption === 'initiation-express' && expressSelected && (
                    <div className="absolute inset-[-3px] rounded-xl border-emerald-500 border-4 animate-border-pulse"></div>
                  )}
                  
                  {/* Metallic Gold Ribbon for Upgrade */}
                  {!expressSelected && (
                    <div className="absolute -top-3 -right-3 z-20">
                      <div className="text-white px-3 py-1 rounded-full shadow-lg transform rotate-12" style={{
                        background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #B8860B 100%)'
                      }}>
                        <span className="text-xs font-bold">⭐ UPGRADE TO EXPRESS</span>
                      </div>
                    </div>
                  )}
                  
                  {selectedFeeOption === 'initiation-express' && expressSelected && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="bg-emerald-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                        <span className="text-3xl font-bold">✓</span>
                      </div>
                    </div>
                  )}
                  <div className="relative text-center mb-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-white font-bold text-xs mb-2 ${expressSelected ? 'bg-emerald-500' : ''}`}
                      style={!expressSelected ? { backgroundColor: '#D4AF37' } : {}}
                    >
                      EXPEDITED
                    </span>
                    <h4 className={`font-bold text-base ${expressSelected ? 'text-gray-900' : ''}`}
                      style={!expressSelected ? { color: '#8B7355' } : {}}
                    >
                      Initiation Fee + Express Fee
                    </h4>
                  </div>
                  <p className={`text-sm ${expressSelected ? 'text-gray-600' : ''} text-center mb-2 font-semibold`}
                    style={!expressSelected ? { color: '#8B7355' } : {}}
                  >
                    {!expressSelected ? (
                      <>
                        🚀 Upgrade to VIP!<br/><br/>Jump the queue and get priority processing!
                      </>
                    ) : (
                      'Pay initiation fee + express fee upfront to jump the queue—be a VIP!'
                    )}
                  </p>
                  {expressSelected && (
                    <>
                      <p className="text-sm text-gray-600 text-center mb-3">
                        Pay <span className="font-bold underline">R{formatCurrency(pricing.initiationFee + pricing.expressFee)}</span> today and secure your 7-day submission timeline
                      </p>
                      
                      {/* Due Later Section */}
                      <div className="mt-3 pt-3 border-t border-emerald-300">
                        <p className="text-xs text-center text-gray-600 font-semibold mb-1">Due Later:</p>
                        <div className="text-xs text-center text-gray-600 space-y-0.5">
                          <p>Submission Fee: <span className="font-semibold">R{formatCurrency(pricing.submissionFee)}</span> ✅</p>
                          {clientData.selectedPackage !== 'bronze' && (
                            <p>Refund Fee: <span className="font-semibold">10% of refund</span> ✅</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {!expressSelected && (
                    <p className="text-xs text-center mt-2 font-bold" style={{ color: '#8B7355' }}>
                      Click here to upgrade for only R{formatCurrency(pricing.expressFeeAmount)}!
                    </p>
                  )}
                </div>

                {/* Option 3: All Three Fees */}
                <div
                  data-upfront-fee-id="all-fees"
                  onClick={() => setSelectedFeeOption('all-fees')}
                  className={`
                    relative cursor-pointer rounded-xl p-3 border-2 transition-all duration-300 transform overflow-visible
                    hover:scale-105 hover:shadow-lg
                    ${selectedFeeOption === 'all-fees' ? 'border-purple-500 bg-purple-50 scale-105 shadow-lg' : 'border-gray-300 bg-white'}
                  `}
                >
                  {/* Animated Border for Selected Option */}
                  {selectedFeeOption === 'all-fees' && (
                    <div className="absolute inset-[-3px] rounded-xl border-purple-500 border-4 animate-border-pulse"></div>
                  )}
                  
                  {selectedFeeOption === 'all-fees' && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                        <span className="text-3xl font-bold">✓</span>
                      </div>
                    </div>
                  )}
                  <div className="relative text-center mb-3">
                    <span className="inline-block px-3 py-1 rounded-full bg-purple-500 text-white font-bold text-xs mb-2">
                      ALL-IN DISCOUNT
                    </span>
                    <h4 className="font-bold text-base text-gray-900">All Fees Upfront</h4>
                    <p className="text-xs text-purple-600 font-semibold">Best Value - Save 5%</p>
                  </div>
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Pay initiation fee + submission fee upfront—Save 5%!
                  </p>
                  
                  {/* Pricing Breakdown */}
                  <div className="mt-3 pt-3 border-t border-purple-300">
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between text-gray-600">
                        <span>Initiation Fee:</span>
                        <span>R{formatCurrency(pricing.initiationFee)}</span>
                      </div>
                      {expressSelected && (
                        <div className="flex justify-between text-gray-600">
                          <span>Express Fee:</span>
                          <span>R{formatCurrency(pricing.expressFee)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-gray-600">
                        <span>Submission Fee:</span>
                        <span>R{formatCurrency(pricing.submissionFee)}</span>
                      </div>
                      <div className="flex justify-between text-gray-700 font-semibold pt-1 border-t border-gray-300">
                        <span>Subtotal:</span>
                        <span>R{formatCurrency(pricing.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-semibold">
                        <span>5% Discount:</span>
                        <span>-R{formatCurrency(pricing.discount)}</span>
                      </div>
                      <div className="flex justify-between text-purple-700 font-bold text-sm pt-1 border-t border-purple-400">
                        <span>Total Investment:</span>
                        <span>R{formatCurrency(pricing.total)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-center text-green-600 font-semibold mt-2">
                      💰 Save R{formatCurrency(pricing.discount)} by paying today!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Page Break for PDF - Ensures Payment Methods appear on new page */}
            <div 
              className="pdf-page-break" 
              data-pdf-only="true"
              style={{ 
                pageBreakAfter: 'always',
                display: 'none',
                height: '0',
                margin: '0',
                padding: '0'
              }}
            ></div>

            {/* Conditional rendering of payment methods - only show if fee option selected */}
            {selectedFeeOption && (
              <>
                {/* Separator Line */}
                <div className="border-t border-gray-300 mb-8 mt-8"></div>
                
                {/* Introduction */}
                <div className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                  <p>
                    We offer multiple payment options to suit your needs. Options are listed from most cost-effective 
                    to most flexible. Choose the method that works best for you.
                  </p>
                </div>

                {/* Payment Method Cards Grid */}
                <div className="mb-8">
                  <h3 className="font-bold text-lg underline mb-4">SELECT YOUR PAYMENT METHOD</h3>
                  <p className="text-sm text-gray-600 mb-8">Click on a payment method to select it.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    data-payment-id={method.id}
                    onClick={() => handleMethodClick(method.id)}
                    className={`
                      relative cursor-pointer rounded-xl overflow-visible transition-all duration-300 transform
                      hover:scale-105 hover:z-10 hover:shadow-2xl
                      ${selectedMethod === method.id ? 'scale-110 z-20 shadow-2xl' : ''}
                      ${method.bgColor}
                    `}
                  >
                    {/* Animated Border for Selected Method */}
                    {selectedMethod === method.id && (
                      <div className={`absolute inset-[-3px] rounded-xl ${method.borderColor} border-4 animate-border-pulse`}></div>
                    )}
                    
                    <div className={`relative p-2 border-2 rounded-xl ${selectedMethod === method.id ? method.borderColor + ' border-4' : 'border-gray-200'} ${method.bgColor} h-full`}>
                      {/* Badge */}
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <span className={`${method.badgeColor} text-white text-base font-bold px-4 py-1.5 rounded-full whitespace-nowrap`}>
                          {method.badge}
                        </span>
                      </div>
                      
                      {/* Method Header */}
                      <div className={`text-center mb-3 pb-3 border-b ${method.borderColor} mt-2`}>
                        <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${method.color} text-white font-bold text-xs mb-1`}>
                          {method.name}
                        </div>
                        <p className={`text-xs ${method.textColor} font-semibold`}>{method.subtitle}</p>
                      </div>
                      
                      {/* Description */}
                      <p className="text-xs text-gray-600 mb-3">
                        {method.description.includes(':') ? (
                          <>
                            <span className="font-bold">{method.description.split(':')[0]}:</span>
                            {method.description.split(':')[1]}
                          </>
                        ) : method.description}
                      </p>
                      
                      {/* Benefits */}
                      <div className="mb-3">
                        <ul className="text-xs space-y-1">
                          {/* Use dynamic benefits for specific payment methods */}
                          {(method.id === 'debit-order' ? getDebitOrderBenefits() : 
                            method.id === 'layby-combo' ? getLaybyBenefits() : 
                            method.benefits).map((benefit, idx) => {
                            let displayBenefit = benefit;
                            
                            // Check if benefit has a colon to bold the prefix
                            const hasBoldPrefix = displayBenefit.includes(':');
                            const parts = hasBoldPrefix ? displayBenefit.split(':') : [displayBenefit];
                            
                            // Check if benefit contains HTML tags for bold and underline
                            const hasHtmlTags = (displayBenefit.includes('<b><u>') && displayBenefit.includes('</u></b>')) || 
                                               (displayBenefit.includes('<em><u>') && displayBenefit.includes('</u></em>'));
                            
                            return (
                              <li key={idx} className="flex items-start gap-1">
                                <span className="text-green-600 mt-0.5">✓</span>
                                <span className="text-gray-700">
                                  {hasBoldPrefix ? (
                                    <>
                                      <span className={`font-bold ${method.textColor}`}>{parts[0]}:</span>
                                      {hasHtmlTags ? (
                                        <span dangerouslySetInnerHTML={{ 
                                          __html: parts[1]
                                            .replace(
                                              /<b><u>(.*?)<\/u><\/b>/g, 
                                              '<span class="font-bold underline">$1</span>'
                                            )
                                            .replace(
                                              /<em><u>(.*?)<\/u><\/em>/g,
                                              '<span class="italic underline">$1</span>'
                                            )
                                        }} />
                                      ) : parts[1]}
                                    </>
                                  ) : displayBenefit}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      
                      {/* Warnings (for Manual EFT) */}
                      {method.warnings && (
                        <div className="mb-3">
                          <ul className="text-xs space-y-1">
                            {method.warnings.map((warning, idx) => (
                              <li key={idx} className="flex items-start gap-1">
                                <span className="text-amber-600 mt-0.5">⚠</span>
                                <span className="text-gray-500">{warning}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Pricing */}
                      <div className={`mt-auto pt-3 border-t ${method.borderColor}`}>
                        <p className="text-xs text-gray-600 font-bold">{method.pricing.label}:</p>
                        <p className={`font-bold text-lg ${method.textColor}`}>
                          {method.isDynamic ? (
                            // Dynamic pricing based on selected fee option
                            <>
                              {selectedFeeOption === 'initiation-only' ? `R${formatCurrency(pricing.initiationFee)}` :
                               selectedFeeOption === 'initiation-express' ? `R${formatCurrency(pricing.initiationFee + pricing.expressFee)}` :
                               selectedFeeOption === 'all-fees' ? `R${formatCurrency(pricing.total)}` :
                               method.pricing.amount}
                              {method.id === 'card-payment' && surcharges.cardFee > 0 && <span className="text-sm"> +R{surcharges.cardFee}</span>}
                              {method.id === 'debit-order' && surcharges.debitOrderFee > 0 && <span className="text-sm"> +R{surcharges.debitOrderFee}</span>}
                              {method.id === 'layby-combo' && surcharges.laybyFee > 0 && (
                                <>
                                  <span className="text-sm"> +R{surcharges.laybyFee}</span>
                                  <span className="text-xs text-gray-400 italic ml-1">(split over 2, 3 or 4 payments)</span>
                                </>
                              )}
                            </>
                          ) : method.pricing.amount}
                        </p>
                        {method.pricing.originalAmount && (
                          <p className="text-xs text-gray-500 line-through">{method.pricing.originalAmount}</p>
                        )}
                        {method.pricing.savings && (
                          <p className="text-xs text-green-600 font-semibold">{method.pricing.savings}</p>
                        )}
                        {/* Dynamic pricing notes */}
                        {method.id === 'card-payment' && (
                          <p className="text-gray-500 mt-1 italic" style={{ fontSize: '10px' }}>
                            {getCardPaymentNote()}
                          </p>
                        )}
                        {method.id === 'debit-order' && (
                          <p className="text-gray-500 mt-1 italic" 
                             style={{ fontSize: '10px' }}
                             dangerouslySetInnerHTML={{ 
                               __html: getDebitOrderNote()
                                 .replace(/Want to save R[\d,.]+\?/g, (match) => `<strong>${match}</strong>`)
                                 .replace(/Note:/g, '<strong>Note:</strong>')
                                 .replace(/<small><u>\(if applicable\)<\/u><\/small>/g, '<span style="font-size: 8px;"><u>(if applicable)</u></span>')
                             }} 
                          />
                        )}
                        {method.id !== 'card-payment' && method.id !== 'debit-order' && method.pricing.note && method.pricing.note !== '' && (
                          <p className="text-gray-500 mt-1 italic" 
                             style={{ fontSize: '10px' }}
                             dangerouslySetInnerHTML={{ 
                               __html: method.pricing.note
                                 .replace(/Note:/g, '<strong>Note:</strong>')
                             }} 
                          />
                        )}
                        
                        {/* Returning Client Discount - Only for INSTANT EFT and existing clients */}
                        {method.id === 'instant-eft' && clientData.clientType === 'existing' && method.isDynamic && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-xs font-semibold" style={{ color: 'hsl(142, 76%, 36%)' }}>
                              🎉 Returning Client Discount!
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'hsl(142, 76%, 36%)' }}>
                              <span className="font-bold">
                                New Total: R
                                {selectedFeeOption === 'initiation-only' ? formatCurrency(Math.round(pricing.initiationFee * 0.95)) :
                                 selectedFeeOption === 'initiation-express' ? formatCurrency(Math.round((pricing.initiationFee + pricing.expressFee) * 0.95)) :
                                 selectedFeeOption === 'all-fees' ? formatCurrency(Math.round(pricing.total * 0.95)) :
                                 '0'}
                              </span>
                              <span className="ml-1 text-xs">(5% off)</span>
                            </p>
                            <p className="text-xs mt-1 italic" style={{ color: 'hsl(142, 76%, 36%)' }}>
                              Use the coupon code <strong>(sent via WhatsApp to you)</strong> on the payment page to claim your discount.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Selected Indicator */}
                      {selectedMethod === method.id && (
                        <div className="absolute top-2 right-2">
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${method.color} flex items-center justify-center shadow-lg`}>
                            <span className="text-white text-3xl font-bold">✓</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selection Summary */}
            {selectedMethod && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">
                  <strong>Selected Payment Method:</strong> {getSelectedMethodDetails()?.name} - {getSelectedMethodDetails()?.subtitle}
                </p>
                <p className="text-green-800">
                  <strong>Total Investment:</strong> {getDisplayedAmount()}
                  {getSelectedMethodDetails()?.id === 'layby-combo' && surcharges.laybyFee > 0 && (
                    <span className="text-gray-500 italic text-sm ml-1">(split over 2, 3 or 4 payments)</span>
                  )}
                </p>
              </div>
            )}

            {/* Action Buttons - Hidden in PDF */}
              </>
            )}

            {/* Navigation Buttons - always visible */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mt-8 pt-6 border-t" data-hide-in-pdf="true">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="sm:w-auto"
              >
                ← Back to Page 5
              </Button>
              
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={!selectedMethod || isGeneratingPDF}
                className="sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={6} totalPages={7} pageTitle="Convenient Payment Methods" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-tca-blue flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Confirm Payment Method
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  You have selected <strong>{getSelectedMethodDetails()?.name}</strong> as your payment method.
                </p>
                
                {/* LAY-BY Specific Content */}
                {getSelectedMethodDetails()?.id === 'layby-combo' ? (
                  <>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="font-semibold text-blue-800">Total Investment: {getDisplayedAmount()}</p>
                    </div>
                    
                    {/* Orange Installment Selection Box */}
                    <div className="bg-orange-50 border-2 border-orange-500 p-4 rounded-lg">
                      <p className="font-semibold text-orange-800 mb-3 text-center">Choose Your Installment Plan</p>
                      
                      <div className="space-y-2">
                        {/* Weekly Options */}
                        <div 
                          onClick={() => setSelectedInstallment('weekly-3')}
                          className={`relative cursor-pointer p-2 rounded border-2 transition-all overflow-visible ${
                            selectedInstallment === 'weekly-3' 
                              ? 'border-orange-600 bg-orange-600' 
                              : 'border-orange-300 bg-white hover:border-orange-400'
                          }`}
                        >
                          {/* Animated Border for Selected Option */}
                          {selectedInstallment === 'weekly-3' && (
                            <div className="absolute inset-[-3px] rounded border-orange-600 border-4 animate-border-pulse"></div>
                          )}
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${selectedInstallment === 'weekly-3' ? 'text-white' : 'text-gray-800'}`}>Weekly - 3 Payments</span>
                            <span className={`text-sm font-bold ${selectedInstallment === 'weekly-3' ? 'text-white' : 'text-orange-700'}`}>
                              {(() => {
                                const method = getSelectedMethodDetails();
                                let baseAmount = 0;
                                if (selectedFeeOption === 'initiation-only') baseAmount = pricing.initiationFee;
                                else if (selectedFeeOption === 'initiation-express') baseAmount = pricing.initiationFee + pricing.expressFee;
                                else if (selectedFeeOption === 'all-fees') baseAmount = pricing.total;
                                const total = baseAmount + (method?.splitFee || 0);
                                const perPayment = Math.ceil(total / 3);
                                return `R${formatCurrency(perPayment)} × 3 weeks`;
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        <div 
                          onClick={() => setSelectedInstallment('weekly-4')}
                          className={`relative cursor-pointer p-2 rounded border-2 transition-all overflow-visible ${
                            selectedInstallment === 'weekly-4' 
                              ? 'border-orange-600 bg-orange-600' 
                              : 'border-orange-300 bg-white hover:border-orange-400'
                          }`}
                        >
                          {/* Animated Border for Selected Option */}
                          {selectedInstallment === 'weekly-4' && (
                            <div className="absolute inset-[-3px] rounded border-orange-600 border-4 animate-border-pulse"></div>
                          )}
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${selectedInstallment === 'weekly-4' ? 'text-white' : 'text-gray-800'}`}>Weekly - 4 Payments</span>
                            <span className={`text-sm font-bold ${selectedInstallment === 'weekly-4' ? 'text-white' : 'text-orange-700'}`}>
                              {(() => {
                                const method = getSelectedMethodDetails();
                                let baseAmount = 0;
                                if (selectedFeeOption === 'initiation-only') baseAmount = pricing.initiationFee;
                                else if (selectedFeeOption === 'initiation-express') baseAmount = pricing.initiationFee + pricing.expressFee;
                                else if (selectedFeeOption === 'all-fees') baseAmount = pricing.total;
                                const total = baseAmount + (method?.splitFee || 0);
                                const perPayment = Math.ceil(total / 4);
                                return `R${formatCurrency(perPayment)} × 4 weeks`;
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Monthly Options */}
                        <div 
                          onClick={() => setSelectedInstallment('monthly-2')}
                          className={`relative cursor-pointer p-2 rounded border-2 transition-all overflow-visible ${
                            selectedInstallment === 'monthly-2' 
                              ? 'border-orange-600 bg-orange-600' 
                              : 'border-orange-300 bg-white hover:border-orange-400'
                          }`}
                        >
                          {/* Animated Border for Selected Option */}
                          {selectedInstallment === 'monthly-2' && (
                            <div className="absolute inset-[-3px] rounded border-orange-600 border-4 animate-border-pulse"></div>
                          )}
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${selectedInstallment === 'monthly-2' ? 'text-white' : 'text-gray-800'}`}>Monthly - 2 Payments</span>
                            <span className={`text-sm font-bold ${selectedInstallment === 'monthly-2' ? 'text-white' : 'text-orange-700'}`}>
                              {(() => {
                                const method = getSelectedMethodDetails();
                                let baseAmount = 0;
                                if (selectedFeeOption === 'initiation-only') baseAmount = pricing.initiationFee;
                                else if (selectedFeeOption === 'initiation-express') baseAmount = pricing.initiationFee + pricing.expressFee;
                                else if (selectedFeeOption === 'all-fees') baseAmount = pricing.total;
                                const total = baseAmount + (method?.splitFee || 0);
                                const perPayment = Math.ceil(total / 2);
                                return `R${formatCurrency(perPayment)} × 2 months`;
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        <div 
                          onClick={() => setSelectedInstallment('monthly-3')}
                          className={`relative cursor-pointer p-2 rounded border-2 transition-all overflow-visible ${
                            selectedInstallment === 'monthly-3' 
                              ? 'border-orange-600 bg-orange-600' 
                              : 'border-orange-300 bg-white hover:border-orange-400'
                          }`}
                        >
                          {/* Animated Border for Selected Option */}
                          {selectedInstallment === 'monthly-3' && (
                            <div className="absolute inset-[-3px] rounded border-orange-600 border-4 animate-border-pulse"></div>
                          )}
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${selectedInstallment === 'monthly-3' ? 'text-white' : 'text-gray-800'}`}>Monthly - 3 Payments</span>
                            <span className={`text-sm font-bold ${selectedInstallment === 'monthly-3' ? 'text-white' : 'text-orange-700'}`}>
                              {(() => {
                                const method = getSelectedMethodDetails();
                                let baseAmount = 0;
                                if (selectedFeeOption === 'initiation-only') baseAmount = pricing.initiationFee;
                                else if (selectedFeeOption === 'initiation-express') baseAmount = pricing.initiationFee + pricing.expressFee;
                                else if (selectedFeeOption === 'all-fees') baseAmount = pricing.total;
                                const total = baseAmount + (method?.splitFee || 0);
                                const perPayment = Math.ceil(total / 3);
                                return `R${formatCurrency(perPayment)} × 3 months`;
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        <div 
                          onClick={() => setSelectedInstallment('monthly-4')}
                          className={`relative cursor-pointer p-2 rounded border-2 transition-all overflow-visible ${
                            selectedInstallment === 'monthly-4' 
                              ? 'border-orange-600 bg-orange-600' 
                              : 'border-orange-300 bg-white hover:border-orange-400'
                          }`}
                        >
                          {/* Animated Border for Selected Option */}
                          {selectedInstallment === 'monthly-4' && (
                            <div className="absolute inset-[-3px] rounded border-orange-600 border-4 animate-border-pulse"></div>
                          )}
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${selectedInstallment === 'monthly-4' ? 'text-white' : 'text-gray-800'}`}>Monthly - 4 Payments</span>
                            <span className={`text-sm font-bold ${selectedInstallment === 'monthly-4' ? 'text-white' : 'text-orange-700'}`}>
                              {(() => {
                                const method = getSelectedMethodDetails();
                                let baseAmount = 0;
                                if (selectedFeeOption === 'initiation-only') baseAmount = pricing.initiationFee;
                                else if (selectedFeeOption === 'initiation-express') baseAmount = pricing.initiationFee + pricing.expressFee;
                                else if (selectedFeeOption === 'all-fees') baseAmount = pricing.total;
                                const total = baseAmount + (method?.splitFee || 0);
                                const perPayment = Math.ceil(total / 4);
                                return `R${formatCurrency(perPayment)} × 4 months`;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {!selectedInstallment && (
                        <p className="text-xs text-orange-700 mt-2 text-center font-medium">
                          Please select an installment option above
                        </p>
                      )}
                    </div>
                    
                    <p className="text-gray-600">
                      A new window will now open for you to complete your payment on the Paystack secure platform. 
                      Please complete your payment and return to this page.
                    </p>
                    <p className="font-medium text-gray-700">
                      Are you happy to proceed with this payment method?
                    </p>
                  </>
                ) : (
                  /* Default Content for Other Payment Methods */
                  <>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="font-semibold text-blue-800">Total Investment: {getDisplayedAmount()}</p>
                    </div>
                    <p className="text-gray-600">
                      A new window will now open for you to complete your payment on the Paystack secure platform. 
                      Please complete your payment and return to this page.
                    </p>
                    <p className="font-medium text-gray-700">
                      Are you happy to proceed with this payment method?
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedInstallment(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmPayment}
              disabled={getSelectedMethodDetails()?.id === 'layby-combo' && !selectedInstallment}
              className={getSelectedMethodDetails()?.id === 'layby-combo' && !selectedInstallment ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Yes, Open Payment Window
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Timer Dialog */}
      <AlertDialog open={showTimerDialog} onOpenChange={() => {}}>
        <AlertDialogContent className="max-w-2xl w-[95vw] sm:w-full">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-tca-blue flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Payment In Progress
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  A new window has opened for you to complete your payment. Please complete your payment on the Paystack platform and return here.
                </p>
                <div className="bg-blue-50 p-6 rounded-lg text-center">
                  <p className="text-gray-600 mb-2">Time Remaining</p>
                  <p className="text-4xl font-bold text-tca-blue">{formatTime(timeRemaining)}</p>
                </div>
                <p className="text-gray-600 text-center">
                  Please complete your payment before the timer expires.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
            <Button 
              variant="outline"
              onClick={() => {
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                }
                setShowTimerDialog(false);
              }}
              className="w-full sm:w-auto sm:min-w-[120px]"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const paymentLink = getDynamicPaymentLink();
                if (paymentLink) {
                  window.open(paymentLink, '_blank');
                }
              }}
              className="w-full sm:w-auto sm:min-w-[180px]"
            >
              Re-open Payment Window
            </Button>
            <AlertDialogAction 
              onClick={() => {
                setShowTimerDialog(false);
                setShowPaymentCompleteDialog(true);
              }}
              className="w-full sm:w-auto sm:min-w-[180px] bg-green-600 hover:bg-green-700"
            >
              I&apos;ve Completed Payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Complete Dialog */}
      {/* Manual EFT Priority Warning Modal */}
      <AlertDialog open={showEftWarningModal} onOpenChange={setShowEftWarningModal}>
        <AlertDialogContent className="max-w-sm" data-testid="eft-warning-modal">
          <AlertDialogHeader className="min-w-0">
            <AlertDialogTitle className="text-gray-800 flex items-center gap-2 text-xl">
              <TrendingDown className="h-6 w-6 text-amber-600" />
              Before You Choose Manual EFT...
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm sm:text-base text-gray-700 min-w-0 w-full">
                <p>
                  Manual EFT lets you pay in your own time - but please understand how our submission
                  queue actually works before you lock this in:
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <Crown className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="min-w-0 flex-1"><strong>Paid clients go first.</strong> Clients who pay upfront are prioritised on our submission list - even if they applied after you.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <TrendingDown className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="min-w-0 flex-1"><strong>Your spot isn't secured.</strong> If you don't pay today, you'll keep sliding down the list every time another client pays upfront and moves ahead of you.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <MessageSquareOff className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="min-w-0 flex-1"><strong>No daily WhatsApp follow-ups.</strong> That priority treatment is reserved for clients who've paid - Manual EFT spots don't carry it until funds reflect.</p>
                  </div>
                </div>

                <p className="text-gray-600 italic">
                  We genuinely want to get your tax return sorted as quickly as possible, and paying today is the simplest way to make that happen.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2 sm:justify-start sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleAcknowledgeEftRisk}
              data-testid="acknowledge-eft-risk-button"
              className="w-full"
            >
              I understand and will EFT as soon as possible
            </Button>
            <Button
              type="button"
              onClick={handleChooseAnotherPaymentMethod}
              data-testid="choose-another-payment-button"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
            >
              Let me choose another payment option
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showPaymentCompleteDialog} onOpenChange={setShowPaymentCompleteDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-tca-blue flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Payment Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  Was your payment successful? Can we proceed to the next and final page?
                </p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-600">
                    If you need more time to complete your payment, you can extend the timer.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={handleExtendTimer}
              className="w-full sm:w-auto"
            >
              Extend Timer (+2 min)
            </Button>
            <AlertDialogAction 
              onClick={handlePaymentSuccess}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            >
              Yes, Payment Was Successful
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual EFT Dialog */}
      <AlertDialog open={showManualEFTDialog} onOpenChange={setShowManualEFTDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Manual EFT Payment Details
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-amber-800 font-semibold">⚠ Important Notice</p>
                  <p className="text-amber-700 text-xs mt-1">
                    This application is valid for 8 hours only. If proof of payment is not received within 8 hours, 
                    this quote expires and you will need to start over.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="font-semibold text-gray-800">Bank Details:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-600">Bank:</span>
                    <span className="font-medium">First National Bank</span>
                    <span className="text-gray-600">Account Name:</span>
                    <span className="font-medium">TC Auditors (Pty) Ltd</span>
                    <span className="text-gray-600">Account Number:</span>
                    <span className="font-medium">62560222871</span>
                    <span className="text-gray-600">Branch Code:</span>
                    <span className="font-medium">250655</span>
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-medium">Your ID Number</span>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-800 text-xs">
                    <strong>Total Investment:</strong> R750 (Initiation) or R1,425 (All-In Option)
                  </p>
                  <p className="text-blue-700 text-xs mt-1">
                    Please email your Proof of Payment to <strong>admin@tcauditors.co.za</strong>
                  </p>
                  <p className="text-blue-600 text-xs mt-2 italic">
                    Or upload your PDF Proof of Payment using the green button below.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
            <Button 
              onClick={handleUploadClick}
              disabled={isUploading}
              className="w-full sm:w-auto bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white font-semibold"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Let Me Upload the PoP Now
                </>
              )}
            </Button>
            <AlertDialogAction onClick={handleManualEFTConfirm} className="w-full sm:w-auto">
              I Understand, I Will Email PoP
            </AlertDialogAction>
          </AlertDialogFooter>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Success Dialog */}
      <AlertDialog open={showUploadSuccessDialog} onOpenChange={setShowUploadSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Upload Successful!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  Your Proof of Payment has been uploaded successfully.
                </p>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-green-800 font-semibold">File saved as:</p>
                  <p className="text-green-700 text-xs mt-1 font-mono break-all">{uploadedFilename}</p>
                </div>
                <p className="text-gray-600">
                  You can now proceed to the final page to complete your agreement.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleUploadSuccessConfirm} className="bg-green-600 hover:bg-green-700">
              Continue to Final Page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Express Upgrade Dialog */}
      <AlertDialog open={showExpressUpgradeDialog} onOpenChange={setShowExpressUpgradeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ color: '#D4AF37' }}>
              <span className="text-3xl">⭐</span>
              Upgrade to VIP Express Service
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <div className="p-4 rounded-lg border-2" style={{
                  background: 'linear-gradient(135deg, #FFF9E6 0%, #FFFAF0 100%)',
                  borderColor: '#D4AF37'
                }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#8B7355' }}>
                    🚀 Welcome to VIP Status!
                  </p>
                  <p style={{ color: '#8B7355' }}>
                    Receive priority processing and submission of your tax return within <strong>7 business days</strong>. 
                    Jump the queue and get ahead of other clients—achieve tax compliance faster!
                  </p>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-blue-800 font-semibold">Upgrade Investment:</p>
                  <p className="text-blue-900 text-2xl font-bold mt-1">
                    R{formatCurrency(pricing.expressFeeAmount)}
                  </p>
                  <p className="text-blue-700 text-xs mt-1">
                    One-time upgrade fee for express service
                  </p>
                </div>

                <div className="text-gray-700 text-xs space-y-2">
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Priority processing in our queue</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>7-day guaranteed submission timeline</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>VIP status throughout your tax journey</span>
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                // Show loading overlay
                setIsUpgradingToExpress(true);
                
                // Close the dialog
                setShowExpressUpgradeDialog(false);
                
                // Small delay to ensure overlay is visible before reload
                setTimeout(() => {
                  // Update the urgency selection to Express
                  navigate('/page-6', {
                    state: {
                      ...clientData,
                      selectedUrgency: 'express'
                    }
                  });
                  
                  // Select the EXPEDITED option
                  setSelectedFeeOption('initiation-express');
                  
                  // Reload to reflect the new express pricing
                  window.location.reload();
                }, 100);
              }}
              className="text-white font-bold"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #B8860B 100%)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #F5C842 0%, #C49A2E 50%, #9A6F09 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #B8860B 100%)';
              }}
            >
              Upgrade to VIP for R{formatCurrency(pricing.expressFeeAmount)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Express Upgrade Loading Overlay */}
      {isUpgradingToExpress && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center">
          <div className="text-center space-y-6">
            {/* Logo */}
            <img 
              src={LOGO_URL} 
              alt="TCA Auditors Logo" 
              className="h-24 w-auto object-contain mx-auto animate-pulse"
            />
            
            {/* Loading Spinner */}
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4" style={{ borderColor: '#D4AF37' }}></div>
            </div>
            
            {/* Message */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-tca-blue">
                Upgrading to VIP Express...
              </h2>
              <p className="text-gray-600 text-sm">
                Please wait while we process your upgrade
              </p>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center gap-2" style={{ color: '#D4AF37' }}>
              <span className="text-2xl animate-bounce">⭐</span>
              <span className="text-2xl animate-bounce delay-100">⭐</span>
              <span className="text-2xl animate-bounce delay-200">⭐</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageSix;
